/**
 * The wire contract between apps and the notifications service. Both sides
 * import these types and paths, so a rename here is a compile error on both.
 *
 * Writes (`notify`, `resolve`, `import`) are sent by an app as its own service
 * identity. Reads and read-state changes are sent on behalf of one person: the
 * app's proxy router mints a service JWT carrying the signed-in user.
 */
import type { InboxApp, InboxBucket, InboxFramework } from '@ordo/constants/notification-rules'

/** Paths on the notifications service. */
export const NOTIFICATIONS_API = {
  notify: '/api/notify',
  resolve: '/api/resolve',
  import: '/api/import',
  list: '/api/notifications',
  counts: '/api/notifications/counts',
  readAll: '/api/notifications/read-all',
  /** One notification, only if it belongs to the token's user. */
  get: (id: string) => `/api/notifications/${encodeURIComponent(id)}`,
  read: (id: string) => `/api/notifications/${encodeURIComponent(id)}/read`,
  unread: (id: string) => `/api/notifications/${encodeURIComponent(id)}/unread`,
  action: (id: string, actionId: string) =>
    `/api/notifications/${encodeURIComponent(id)}/actions/${encodeURIComponent(actionId)}`,
  reply: (id: string) => `/api/notifications/${encodeURIComponent(id)}/reply`,
  /** The right side, fetched live from the owning app. Answers a `NotificationDetailResponse`. */
  detail: (id: string) => `/api/notifications/${encodeURIComponent(id)}/detail`,
  mutes: '/api/mutes',
  pushSubscriptions: '/api/push-subscriptions',
  pushPublicKey: '/api/push/public-key',
  stream: '/api/stream',
  /** The signed-in Ordo team member's own email/push switches (GET, PUT). */
  settings: '/api/settings',
  /** An HQ admin reads or changes one person's switches (GET, PUT). */
  adminSettings: (userId: string) => `/api/admin/settings/${encodeURIComponent(userId)}`,
  /** What new accounts get (GET, PUT). HQ admins only. */
  adminDefaults: '/api/admin/defaults',
} as const

/**
 * The service's VAPID public key, which every app's browser subscribes with.
 * `null` when push is off: the service has no keys, or the app does not offer
 * the inbox's push yet (see `offerPush` on the proxy router).
 */
export interface PushPublicKeyResponse {
  publicKey: string | null
}

/** Marks a web push as the unified inbox's, so a service worker that also handles an app's own push can tell them apart. */
export const INBOX_PUSH_SOURCE = 'ordo-inbox'

/** What the service sends in a web push (`pushPayload` in `apps/notifications/src/lib/inbox.ts`). */
export type InboxPushPayload = {
  source: typeof INBOX_PUSH_SOURCE
  /** The owning app's label, e.g. "Tasks". */
  title: string
  /** The notification sentence as plain text. */
  body: string
  /** Absolute URL of the item in its owning app. */
  url: string
  /** The notification id, so a re-send replaces rather than stacks. */
  tag: string
}

/** One run of text in the notification sentence; `bold` marks names and the item. */
export interface SentenceSegment {
  text: string
  bold?: boolean
}

/**
 * Who the picture shows. A person, else the school or chef involved, else
 * nothing (the bell draws the generic circle). Snapshotted at send time so the
 * bell never has to call five apps to draw a face.
 */
export type NotifySender =
  | { kind: 'person'; id: string; name: string; imageUrl?: string | null }
  | { kind: 'org'; id: string; name: string; imageUrl?: string | null }
  | { kind: 'none' }

/** The thing the notification is about, and where clicking it goes. */
export interface NotifyItem {
  /** e.g. `task`, `deal`, `school`, `pull_request`. Used for "Unfollow {kind}" and item mutes. */
  kind: string
  id: string
  label: string
  /** Absolute URL in the owning app. The bell runs in every app, so a relative path would open in the wrong one. */
  url: string
  /** An optional second destination, e.g. "Open in Factory" next to "Open in GitHub". */
  secondary?: { label: string; url: string }
}

export interface NotifyRecipient {
  /** Multipass user id. */
  id: string
  bucket: InboxBucket
  /** Where email goes. The sending app already knows it; without it the service skips email for this person. */
  email?: string | null
  /** For the email greeting. */
  name?: string | null
}

export interface NotifyInput {
  /** Inbox type key from `INBOX_NOTIFICATION_TYPES`, e.g. `tasks.task_assigned`. */
  type: string
  recipients: NotifyRecipient[]
  /** Multipass id of whoever caused it. Never notified about their own action. */
  actorId?: string | null
  sender: NotifySender
  sentence: SentenceSegment[]
  /** What the person said (comment, note, email first line). Plain text. */
  preview?: string | null
  item: NotifyItem
  /** Raw facts kept for rewording later. Not shown. */
  params?: Record<string, unknown>
  /** Unique per app; retries and backfills never double-send. */
  idempotencyKey: string
}

/** Body the service receives for a write: the input plus which app sent it. */
export interface NotifyRequestBody extends NotifyInput {
  app: InboxApp
}

/** Another recipient answered a decision; every copy of it shows who answered. */
export interface ResolveInput {
  type: string
  itemId: string
  action: 'approve' | 'decline'
  byId: string
  byName: string
}

/** A historical notification copied in at switch-over, keeping its times and read state. */
export interface ImportInput extends NotifyInput {
  createdAt: string
  readAt: string | null
}

/** How a stored notification reads back to the bell. */
export interface InboxNotification {
  id: string
  app: InboxApp
  type: string
  bucket: InboxBucket
  framework: InboxFramework
  sender: NotifySender
  sentence: SentenceSegment[]
  preview: string | null
  item: NotifyItem
  /** One-click answers still open to this person (empty once answered). */
  actions: ('approve' | 'decline')[]
  resolved: { action: 'approve' | 'decline'; byName: string; at: string } | null
  readAt: string | null
  createdAt: string
}

/** Unread counts. The bell shows `total`; each tab pill shows its own. */
export interface InboxCounts {
  assigned: number
  mentioned: number
  following: number
  total: number
}

export interface InboxListResponse {
  items: InboxNotification[]
  nextCursor: string | null
  counts: InboxCounts
  /** True when the app's proxy could not reach the service; the bell shows its unavailable state. */
  unavailable?: boolean
}

/** What `read` and `unread` answer: the notification as it now reads. */
export interface ReadStateResponse {
  ok: true
  notification: InboxNotification
}

export const EMPTY_INBOX_COUNTS: InboxCounts = Object.freeze({ assigned: 0, mentioned: 0, following: 0, total: 0 })

/**
 * The right side of the notifications page. The owning app answers with one
 * framework and only these blocks (data model §4c); the bell adds the header,
 * "Open in {App}" and the Following note itself. Every block is optional.
 */
export interface NotificationDetailMessage {
  who: { name: string; imageUrl?: string | null; kind?: 'person' | 'org' }
  /** Already formatted by the app, e.g. "9:12 AM" or "Oct 14". */
  when: string
  text: string
}

export interface NotificationDetailBlocks {
  message?: NotificationDetailMessage[]
  /** `[label, value]` rows, e.g. `['Amount', '$48,000']`. */
  facts?: [string, string][]
  checklist?: { title: string; rows: [string, boolean][] }
  /** `[label, sub]` rows, e.g. `['Send W-9 to Lakeview', 'Due tomorrow']`. */
  list?: { title: string; rows: [string, string][] }
  change?: { label: string; from: string; to: string }
  errors?: string[]
  compare?: { left: string; right: string; rows: [string, string][] }
  text?: string
}

/** What an app's detail endpoint returns for one notification. */
export interface NotificationDetailContent {
  framework: InboxFramework
  blocks: NotificationDetailBlocks
}

/** One-click answers a notification type can offer (registry `actions`). */
export type NotificationQuickAction = 'approve' | 'decline'

export const NOTIFICATION_QUICK_ACTIONS: readonly NotificationQuickAction[] = ['approve', 'decline']

export function isNotificationQuickAction(value: unknown): value is NotificationQuickAction {
  return (NOTIFICATION_QUICK_ACTIONS as readonly unknown[]).includes(value)
}

/** Longest reply the service forwards to an app. */
export const MAX_NOTIFICATION_REPLY_LENGTH = 5000

/**
 * Paths every owning app (Tasks, RFPs, Customers, Factory) serves on its own
 * origin for the notifications service. The service calls them with a service
 * JWT (`X-Ordo-Internal-Token`) whose audience is the app's slug and whose user
 * is the person acting, so the app re-checks that person's access exactly as
 * its own screens do. The notifications service never changes app data itself.
 */
export const NOTIFICATION_APP_API = {
  /** `GET ?type=&itemKind=&itemId=` → 200 `NotificationDetailContent`, 404 `{ status: 'deleted' }`, 403 `{ status: 'no-access' }`. */
  detail: '/api/service/notification-detail',
  /** `POST NotificationActionRequest` → 200 `NotificationActionResult`, 4xx `NotificationAppError` (shown as the refusal). */
  actions: '/api/service/notification-actions',
  /** `POST NotificationReplyRequest` → 200 `{ ok: true }`, 4xx `NotificationAppError`. */
  reply: '/api/service/notification-reply',
} as const

/** Query of an app's detail endpoint. */
export interface NotificationDetailQuery {
  /** Inbox type key, e.g. `rfps.bond_approval_requested`. */
  type: string
  itemKind: string
  itemId: string
}

/** Body of an app's actions endpoint. `params` is what the sender stored on the notification. */
export interface NotificationActionRequest extends NotificationDetailQuery {
  action: NotificationQuickAction
  params: Record<string, unknown>
}

/** An app's answer to a quick action that went through. */
export interface NotificationActionResult {
  ok: true
  /** The outcome line the bell shows, e.g. "Approved. Wesley has been notified." */
  message: string
  /** Who answered, for every other recipient's "{byName} approved this". */
  byName: string
}

/** Body of an app's reply endpoint. */
export interface NotificationReplyRequest extends NotificationDetailQuery {
  text: string
  params: Record<string, unknown>
}

/** Any 4xx from an app's actions or reply endpoint. `error` is shown to the person as the refusal. */
export interface NotificationAppError {
  error: string
}

/** The bodies an app's detail endpoint answers with on 404 and 403. */
export type NotificationDetailRefusal = { status: 'deleted' } | { status: 'no-access' }

/**
 * What the service's `GET /api/notifications/:id/detail` answers. `generic`
 * means the owning app ships no detail for this notification (the bell draws
 * the Generic framework from what it stored).
 */
export type NotificationDetailResponse =
  | { status: 'ready'; content: NotificationDetailContent }
  | { status: 'generic' }
  | { status: 'deleted' }
  | { status: 'no-access' }
  | { status: 'unavailable' }

/** What the service's `POST /api/notifications/:id/actions/:actionId` answers when the app said yes. */
export interface NotificationActionResponse {
  ok: true
  message: string
}

/**
 * Channels a person can switch. IN_APP (the bell) is listed only for an opt-in
 * type (`optIn` in the inbox registry), where it is the type's on/off; every
 * other type's bell is always on and never listed.
 */
export type ChannelSettingChannel = 'IN_APP' | 'EMAIL' | 'PUSH'

/**
 * One row of a settings page (self, an admin editing someone, or the defaults
 * for new accounts). `channels` lists only the switches that exist for this
 * type: IN_APP only for an opt-in type, no EMAIL when the type has no email
 * template, and PUSH only for Assigned and Mentioned types.
 */
export interface ChannelSettingsRow {
  typeKey: string
  app: InboxApp
  label: string
  description: string
  channels: ChannelSettingChannel[]
  /** Effective on/off per channel (for the defaults page: what a new account gets). */
  values: Partial<Record<ChannelSettingChannel, boolean>>
}

export interface ChannelSettingChange {
  typeKey: string
  channel: ChannelSettingChannel
  enabled: boolean
}

/** Body of every settings PUT. */
export interface ChannelSettingsUpdate {
  changes: ChannelSettingChange[]
}

/** What every settings GET and PUT answers. */
export interface ChannelSettingsResponse {
  items: ChannelSettingsRow[]
}
